// Shared intake-email content. Used by the Landing "tell us what you have"
// link, the cost-screen over-budget off-ramp, and the recover-state
// get-in-touch CTA — so every contact path asks the same questions in the same
// shape and carries the same privacy guardrail. One source of truth.

// Individual intake prompts. The size/count prompt is separable: the
// over-budget off-ramp already states the size in its lead, so it asks the
// other questions but drops this one (see buildIntakeMailto's `includeCount`).
const FIELD_WHAT =
  "What you have (one or more): iMessage, OFW, Civil Communicator, " +
  "email PDFs, WhatsApp, screenshots, an export from a tool not " +
  "listed on the page, etc.";
const FIELD_SPAN = "Time span:";
const FIELD_COUNT = "Rough page count or message count if you know:";
const FIELD_DEADLINE = "Hearing date or deadline (if any):";
const FIELD_NOTES = "Notes:";

function fieldBlock(includeCount: boolean): string {
  const fields = [FIELD_WHAT, FIELD_SPAN];
  if (includeCount) fields.push(FIELD_COUNT);
  fields.push(FIELD_DEADLINE, FIELD_NOTES);
  // Each prompt on its own line, a blank line between; trailing newline after
  // the last so the body doesn't end mid-line.
  return fields.join("\n\n") + "\n";
}

// The full template (size prompt included) — what Landing and the recover CTA
// use, since they don't otherwise know the record's size.
export const INTAKE_FIELDS = fieldBlock(true);

// We never want a survivor to attach their message export to an ordinary email.
// The contact mailtos are for triage only — describe what you have, don't send
// it. This states that plainly and warmly, and is included in every intake
// email so the expectation is set before they reach for the paperclip.
export const NO_ATTACH_NOTE =
  "Please don't attach your messages or any files to this email — ordinary " +
  "email isn't a safe way to send them. Just tell us what you have below, and " +
  "we'll set up a private, secure way for you to share it.";

// Build a prefilled contact mailto (to hello@plaintosee.cc) using the shared
// intake template. When the user reached this from inside the wizard they
// logged in with a minted pilot code; embedding it as a VISIBLE body line lets
// us tie the note to their case in the operator console. It's transparent (they
// see it in the email they send), the code is single-use and already spent by
// then, and it carries NO message content — only the code + what they type.
// `code` is omitted for pre-login contact paths (e.g. the Landing link).
// `includeCount` defaults true; pass false when the lead already states the
// record's size (the over-budget off-ramp) so we don't ask for it twice.
export function buildIntakeMailto(opts: {
  subject: string;
  lead: string;
  code?: string | null;
  includeCount?: boolean;
}): string {
  const trimmed = opts.code?.trim();
  const codeLine = trimmed ? `Access code: ${trimmed}\n\n` : "";
  const body =
    `${opts.lead}\n\n` +
    codeLine +
    `${NO_ATTACH_NOTE}\n\n` +
    "A few details (optional — fill what you can):\n\n" +
    fieldBlock(opts.includeCount ?? true);
  return (
    "mailto:hello@plaintosee.cc?subject=" +
    encodeURIComponent(opts.subject) +
    "&body=" +
    encodeURIComponent(body)
  );
}
